import { styleConstants } from '@paywithextend/components'
import {
  blue200,
  blue300,
  green150,
  green200,
  green300,
  purple250,
  purple300,
  purple400,
} from 'ext-ui/style/colors'
import { font_size_md, font_size_sm } from 'ext-ui/style/typography'
import React from 'react'
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { ConfigContext } from 'src/context/ConfigContext/ConfigContextBase'
import { ExtendCurrency } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import styled, { css } from 'styled-components'

import styles from './InsightsPieChart.style'

/**
 * Data structure for each slice in the InsightsPieChart.
 *
 * Example data format:
 * const mockData: InsightsPieChartData[] = [
 *   { label: 'Flights', value: 1500, usdSpendCents: 150000 },
 *   { label: 'Hotels', value: 1100, usdSpendCents: 110000 },
 *   { label: 'Taxi', value: 600, usdSpendCents: 60000 },
 *   { label: 'Restaurants', value: 950, usdSpendCents: 95000 },
 *   { label: 'Office Supplies', value: 450, usdSpendCents: 45000 },
 * ];
 */
export interface InsightsPieChartData {
  label: string
  name?: string // Alternate label field for backward compatibility
  value?: number
  usdSpendCents?: number
  category?: string
  [key: string]: string | number | undefined
}

export interface InsightsPieChartProps {
  data: InsightsPieChartData[]
  /**
   * Minimum height for the chart. Will grow dynamically based on legend size.
   * Defaults to 300.
   */
  minHeight?: number
  /**
   * Colors for each pie slice. If not provided, uses a default color palette.
   */
  colors?: string[]
  /**
   * Key in data to use for pie slice values. Defaults to 'value'.
   */
  valueKey?: string
  tooltipFormatter?: (
    value: number,
    name: string,
    item: any,
    index: number,
  ) => React.ReactNode
  currency?: ExtendCurrency
  className?: string
}

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

// Default color palette for pie chart slices using ext-ui colors
const defaultColors = [
  purple400, // Primary purple
  purple300, // Light purple
  purple250, // Lighter purple
  blue300, // Blue
  blue200, // Light blue
  green200, // Teal
  green150, // Light teal
  green300, // Green
]

export const getInsightsPieLabel = (
  item: Partial<Pick<InsightsPieChartData, 'label' | 'name'>>,
  unknownLabel: string,
): string => item.label || item.name || unknownLabel

export const formatInsightsPiePercentage = (
  value: number,
  total: number,
  locale: string,
): string =>
  new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(total > 0 ? value / total : 0)

const InsightsPieChart: React.FC<InsightsPieChartProps> = ({
  data,
  minHeight = 300,
  colors = defaultColors,
  valueKey = 'value',
  tooltipFormatter,
  currency = ExtendCurrency.Usd,
  className,
}) => {
  const { locale } = React.useContext(ConfigContext)
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const unknownLabel = translate('Unknown')

  // Normalize data to ensure we have the right value field
  const normalizedData = data.map((item) => {
    const rawValue = item[valueKey] || item.usdSpendCents || 0
    return {
      ...item,
      name: getInsightsPieLabel(item, unknownLabel),
      value: typeof rawValue === 'number' ? rawValue : 0,
    }
  })

  // Calculate total for percentage calculations
  const total = normalizedData.reduce(
    (sum, item) => sum + (item.value as number),
    0,
  )

  // Calculate dynamic height based on number of legend items
  // Base height for chart + legend height (approximately 20px per legend item in wrapped layout)
  // Account for legend wrapping: roughly 3-4 items per row depending on label length
  const legendItemHeight = 20
  const itemsPerRow = 3
  const legendRows = Math.ceil(normalizedData.length / itemsPerRow)
  const chartBaseHeight = 280
  const legendHeight = legendRows * legendItemHeight + 40 // 40px for padding
  const dynamicHeight = Math.max(minHeight, chartBaseHeight + legendHeight)

  // Custom label renderer for pie slices - show category name
  const renderLabel = (entry: any) => {
    const name = getInsightsPieLabel(entry, unknownLabel)
    // Truncate long names to fit on pie slice
    return name.length > 20 ? `${name.substring(0, 18)}...` : name
  }

  return (
    <ChartContainer css={styles} className={className || 'InsightsPieChart'}>
      <ResponsiveContainer
        css={css`
          .recharts-surface {
            overflow: visible;
          }
        `}
        width="100%"
        height={dynamicHeight}
      >
        <PieChart>
          <Pie
            data={normalizedData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="35%"
            outerRadius="80%"
            innerRadius="0%"
            label={renderLabel}
            labelLine={true}
            isAnimationActive={false}
          >
            {normalizedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={
              tooltipFormatter ||
              ((value: number, name: string, item: any, index: number) => {
                // Calculate percentage
                const percent = formatInsightsPiePercentage(
                  value,
                  total,
                  locale,
                )

                // Format as currency if we have usdSpendCents
                const formattedAmount = item.payload.usdSpendCents
                  ? formatCurrency({
                      amount: item.payload.usdSpendCents / 100,
                      currency,
                    })
                  : formatCurrency({ amount: value, currency })

                return `${formattedAmount} (${percent})`
              })
            }
            contentStyle={{
              borderRadius: 8,
              border: 'none',
              boxShadow: '0 2px 8px #0001',
              fontSize: font_size_md,
            }}
          />
          <Legend
            verticalAlign="bottom"
            iconType="circle"
            wrapperStyle={{
              fontSize: font_size_sm,
              color: styleConstants.colors.gray_darkest,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

export default InsightsPieChart
